import axios from "axios";
import { getCookieValue } from "../js/utils/cookies";

// API URL configuration based on environment
// Function to get the API URL dynamically (useful for network IP access)
const getApiUrl = () => {
	// Use environment variable if set
	if (import.meta.env.MODE === "production") {
		return (
			import.meta.env.VITE_PROD_API_URL || "https://goodglows.com/api/goodglows"
		);
	}
	if (import.meta.env.MODE === "staging") {
		return (
			import.meta.env.VITE_STAGING_API_URL ||
			"https://test.goodglows.com/api/goodglows"
		);
	}
	if (
		import.meta.env.MODE === "development" ||
		import.meta.env.MODE === "developmentstaging"
	) {
		if (import.meta.env.VITE_DEV_API_URL) {
			return import.meta.env.VITE_DEV_API_URL;
		}
	}
	if (import.meta.env.MODE === "local") {
		if (import.meta.env.VITE_LOCAL_API_URL) {
			return import.meta.env.VITE_LOCAL_API_URL;
		}
	}

	// For development modes, use dynamic hostname detection
	if (typeof window !== "undefined") {
		const {hostname} = window.location;
		// If accessing via network IP (not localhost), use the same IP for API
		if (hostname !== "localhost" && hostname !== "127.0.0.1") {
			return `http://${hostname}:8000/api`;
		}
	}

	// Default to localhost for development
	return "http://localhost:8000/api";
};

// Get base API URL (used for export and initial setup)
const API_URL = getApiUrl();

// Image path configuration based on environment
const IMAGE_PATH =
	import.meta.env.MODE === "production" ||
	import.meta.env.MODE === "staging" ||
	import.meta.env.MODE === "developmentstaging"
		? import.meta.env.VITE_PROD_IMAGE_PATH || "/static/goodglows/images"
		: import.meta.env.MODE === "development"
			? import.meta.env.VITE_DEV_IMAGE_PATH || "/images"
			: import.meta.env.MODE === "local"
				? import.meta.env.VITE_LOCAL_IMAGE_PATH || "/images"
				: import.meta.env.VITE_DEV_IMAGE_PATH || "/images";

export const MEDIA_PATH =
	import.meta.env.MODE === "production"
		? import.meta.env.VITE_PROD_QR_BASE_URL
		: import.meta.env.MODE === "staging"
			? import.meta.env.VITE_STAGE_QR_BASE_URL
			: import.meta.env.MODE === "development"
				? import.meta.env.VITE_DEV_QR_BASE_URL
				: import.meta.env.VITE_DEV_QR_BASE_URL;

export const SUBTITLE_PATH =
	import.meta.env.MODE === "production" ||
	import.meta.env.MODE === "staging" ||
	import.meta.env.MODE === "developmentstaging"
		? import.meta.env.VITE_PROD_SUBTITLE_PATH || "/static/goodglows/subtitles"
		: import.meta.env.MODE === "development"
			? import.meta.env.VITE_DEV_SUBTITLE_PATH || "/subtitles"
			: import.meta.env.MODE === "local"
				? import.meta.env.VITE_LOCAL_SUBTITLE_PATH || "/subtitles"
				: import.meta.env.VITE_DEV_SUBTITLE_PATH || "/subtitles";

// SESSION_ID getter that dynamically retrieves session ID
export const SESSION_ID = () => {
	return (
		getCookieValue("goodglows_sessionid") ||
		localStorage.getItem("goodglows_sessionid")
	);
};

const VIDEO_PATH =
	import.meta.env.MODE === "production" ||
	import.meta.env.MODE === "staging" ||
	import.meta.env.MODE === "developmentstaging"
		? import.meta.env.VITE_PROD_VIDEO_PATH || "/static/goodglows/videos"
		: import.meta.env.MODE === "development"
			? import.meta.env.VITE_DEV_VIDEO_PATH || "/videos"
			: import.meta.env.MODE === "local"
				? import.meta.env.VITE_LOCAL_VIDEO_PATH || "/videos"
				: import.meta.env.VITE_DEV_VIDEO_PATH || "/videos";

// Configure axios instance
const apiClient = axios.create({
	baseURL: API_URL,
	withCredentials: true,
});

// Add request interceptor to dynamically set baseURL and CSRF token
apiClient.interceptors.request.use(
	(config) => {
		// Dynamically update baseURL for development to support network IP access
		if (
			import.meta.env.MODE === "development" ||
			import.meta.env.MODE === "developmentstaging" ||
			import.meta.env.MODE === "local"
		) {
			if (
				!import.meta.env.VITE_DEV_API_URL &&
				!import.meta.env.VITE_LOCAL_API_URL
			) {
				const dynamicUrl = getApiUrl();
				config.baseURL = dynamicUrl;
				// Debug logging (remove in production)
				if (import.meta.env.DEV) {
					console.log(`[API] Using baseURL: ${dynamicUrl}`);
				}
			}
		}

		const csrfToken = getCookieValue("csrftoken");
		if (csrfToken) {
			config.headers["X-CSRFToken"] = csrfToken;
		}
		return config;
	},
	(error) => {
		return Promise.reject(error);
	}
);

export { API_URL, IMAGE_PATH, apiClient, VIDEO_PATH };
